import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  listDisclaimerVersions,
  createDisclaimerVersion,
  updateDisclaimerVersion,
  getDisclaimerAcceptances,
} from '../../api/disclaimer';
import DWLFLoader from '../../components/DWLFLoader';

const DisclaimerAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [versions, setVersions] = useState([]);
  const [acceptanceSummary, setAcceptanceSummary] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editForm, setEditForm] = useState({
    disclaimerType: 'platform',
    version: '',
    title: '',
    content: '',
    requiresReacceptance: true,
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [versionsData, acceptancesData] = await Promise.all([
        listDisclaimerVersions(),
        getDisclaimerAcceptances(),
      ]);
      setVersions(versionsData);
      setAcceptanceSummary(acceptancesData.summary || []);
    } catch (err) {
      setError(err.message || 'Failed to load disclaimer data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = (disclaimer) => {
    setEditForm({
      disclaimerType: disclaimer.disclaimerType || 'platform',
      version: disclaimer.version || '',
      title: disclaimer.title || '',
      content: disclaimer.content || '',
      requiresReacceptance: disclaimer.requiresReacceptance !== false,
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setEditForm({
      disclaimerType: 'platform',
      version: '',
      title: '',
      content: '',
      requiresReacceptance: true,
    });
    setIsCreating(true);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setEditForm({
      disclaimerType: 'platform',
      version: '',
      title: '',
      content: '',
      requiresReacceptance: true,
    });
  };

  const handleSave = async () => {
    if (!editForm.version || !editForm.title || !editForm.content) {
      setError('Version, title, and content are required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (isCreating) {
        await createDisclaimerVersion(editForm);
        setSuccess(`Disclaimer version ${editForm.version} created successfully`);
      } else {
        await updateDisclaimerVersion(editForm.version, editForm);
        setSuccess(`Disclaimer version ${editForm.version} updated successfully`);
      }
      handleCancel();
      await fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save disclaimer');
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field) => (event) => {
    const value = field === 'requiresReacceptance' ? event.target.checked : event.target.value;
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading && versions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <DWLFLoader size="medium" showText={false} />
      </Box>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
      }}
    >
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Disclaimer Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage legal disclaimers that users must accept before using the platform.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Create new disclaimer version">
              <span>
                <IconButton
                  onClick={handleCreate}
                  disabled={isEditing || isCreating}
                  color="primary"
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={fetchData} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Edit/Create Form */}
        {(isEditing || isCreating) && (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: 'var(--dwlf-ink-02)',
            }}
          >
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              {isCreating ? 'Create New Disclaimer' : 'Edit Disclaimer'}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Disclaimer Type"
                  value={editForm.disclaimerType}
                  onChange={handleFormChange('disclaimerType')}
                  fullWidth
                  size="small"
                  disabled={isEditing}
                  helperText="e.g., platform, trading, beta"
                />
                <TextField
                  label="Version"
                  value={editForm.version}
                  onChange={handleFormChange('version')}
                  fullWidth
                  size="small"
                  disabled={isEditing}
                  helperText="e.g., 1.0.0"
                />
              </Stack>

              <TextField
                label="Title"
                value={editForm.title}
                onChange={handleFormChange('title')}
                fullWidth
                size="small"
                helperText="Display title for the disclaimer"
              />

              <TextField
                label="Content"
                value={editForm.content}
                onChange={handleFormChange('content')}
                fullWidth
                multiline
                rows={12}
                size="small"
                helperText="Full disclaimer text (supports HTML)"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.requiresReacceptance}
                    onChange={handleFormChange('requiresReacceptance')}
                    color="primary"
                  />
                }
                label="Require users to re-accept when updated"
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <DWLFLoader size="tiny" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || !editForm.version || !editForm.title || !editForm.content}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Versions Table */}
        {versions.length === 0 && !isCreating ? (
          <Alert severity="info">
            No disclaimers configured yet. Click the + button to create one.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'transparent' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Re-acceptance</TableCell>
                  <TableCell>Effective From</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((disclaimer) => (
                  <TableRow key={`${disclaimer.disclaimerType}-${disclaimer.version}`} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {disclaimer.disclaimerType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{disclaimer.version}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {disclaimer.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={disclaimer.requiresReacceptance ? 'success.main' : 'text.secondary'}
                      >
                        {disclaimer.requiresReacceptance ? 'Yes' : 'No'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {disclaimer.effectiveFrom
                          ? new Date(disclaimer.effectiveFrom).toLocaleDateString()
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {disclaimer.updatedAt
                          ? new Date(disclaimer.updatedAt).toLocaleDateString()
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit disclaimer">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(disclaimer)}
                            disabled={isEditing || isCreating}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Acceptance Summary */}
        {acceptanceSummary.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Acceptance Summary
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'transparent' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Acceptances</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {acceptanceSummary.map((item, idx) => (
                    <TableRow key={`${item.disclaimerType}-${item.version}-${idx}`} hover>
                      <TableCell>{item.disclaimerType}</TableCell>
                      <TableCell>{item.version}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell>{item.acceptanceCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DisclaimerAdmin;
